import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useProductsSearchBar from '@/shared/components/widgets/ProductsSearchBar/hooks/useProductsSearchBar';
import DialogName from '@/shared/types/dialogName.enum';
import ProductOrder from '@/shared/types/productOrder.type';
import ProductVariant from '@/shared/types/products/productVariant.type';

import useCart from '../../components/Cart/hooks/useCart';
import useTile from '../../components/Tile/hooks/useTile';
import UpdateHandlers from '../../types/updateHandlers.type';
import useDefaultSettings from './useDefaultSettings';

const defineVariantValue = (variant: ProductVariant) => {
  if (variant.product_prices) {
    return variant.product_prices.map((item) => ({
      ...item,
      value: parseFloat(item.value).toFixed(2),
    }));
  }

  return variant.value.toFixed(2);
};

const transformVariantsToProductOrder = (
  variant: ProductVariant,
): ProductOrder => {
  const {
    variant_id,
    product_id,
    variant_name,
    product_name,
    sku,
    in_account_id,
    in_order_id,
    in_tenant_id,
    post_tax_price,
    pre_tax_price,
    tax_multiplier,
    product_description,
  } = variant;

  return {
    id: variant_id || product_id,
    name: variant_name || product_name,
    price: defineVariantValue(variant),
    product_qty: '1',
    sku: sku,
    product_id: product_id,
    variant_id: variant_id,
    product_name: product_name,
    variant_name: variant_name,
    in_account_id: in_account_id,
    in_order_id: in_order_id,
    in_tenant_id: in_tenant_id,
    post_tax_price: post_tax_price,
    pre_tax_price: pre_tax_price,
    product_description: product_description,
    tax_multiplier: tax_multiplier,
    value: defineVariantValue(variant),
  } as ProductOrder;
};

type Args = {
  dialogProps: UpdateHandlers<unknown>;
  defaultSettingsState: ReturnType<typeof useDefaultSettings>;
  cartState: ReturnType<typeof useCart>;
};

const useCheckoutMain = ({
  dialogProps,
  defaultSettingsState,
  cartState,
}: Args) => {
  const { t } = useTranslation(['common']);
  const searchOverProductsState = useProductsSearchBar();
  const selectedVariantValueRef = useRef<ProductVariant | null>(null);

  const tileState = useTile({
    listName: 'checkout-main',
    updateDialogStates: dialogProps,
  });

  const [isWarningVisible, setIsWarningVisible] = useState(false);

  useEffect(() => {
    const { barcodeScannerProps, selectedValue } = searchOverProductsState;

    if (barcodeScannerProps.searchingStatus === 'searching') {
      dialogProps.onUpdateProgressDialog({
        title: t('common:searching'),
        progress: 0,
        open: true,
      });
      dialogProps.onUpdateDialogState(DialogName.SearchProductNotFound, {
        open: false,
      });
    }

    if (barcodeScannerProps.searchingStatus === 'complete') {
      dialogProps.onUpdateProgressDialog({ open: false });

      dialogProps.onUpdateDialogState(DialogName.SearchProductNotFound, {
        open: !barcodeScannerProps.product,
      });

      if (barcodeScannerProps.product) {
        cartState.addProductToCart(barcodeScannerProps.product);
      }
    }

    if (
      selectedValue &&
      !isEqual(selectedValue, selectedVariantValueRef.current)
    ) {
      selectedVariantValueRef.current = selectedValue;

      const productOrder = transformVariantsToProductOrder(selectedValue);
      cartState.addProductToCart(productOrder);
    }
  }, [t, dialogProps, cartState, searchOverProductsState]);

  useEffect(() => {
    const { searchBoxProps } = tileState.tileActionHandlers['assign-customer'];

    if (
      searchBoxProps.assignedCustomer &&
      searchBoxProps.assignedCustomer?.in_customer_id !==
        cartState.assignedCustomer?.in_customer_id
    ) {
      cartState.onAssignCustomer(searchBoxProps.assignedCustomer);
    }
  }, [cartState, tileState.tileActionHandlers]);

  useEffect(() => {
    const { defaultCustomer, defaultSettings, isLoading } =
      defaultSettingsState;

    if (
      !isLoading &&
      (!defaultSettings.in_customer_id || !defaultSettings.shipping_profile_id)
    ) {
      setIsWarningVisible(true);
    } else {
      setIsWarningVisible(false);
    }

    if (defaultCustomer && !cartState.assignedCustomer) {
      cartState.onAssignCustomer(defaultCustomer);
    }
  }, [cartState, defaultSettingsState]);

  return {
    searchOverProductsState,
    tileState,
    isWarningVisible,
  };
};

export default useCheckoutMain;
